/**
 * GraphQL Client Utilities
 * 
 * This module provides GraphQL client functionality for the frontend-tickets application.
 * It includes query definitions and client configuration for communicating with the backend GraphQL endpoint.
 * 
 * @module utils/graphql
 */

import { GraphQLClient, gql } from 'graphql-request';
import config from '../config/config';

// Timeout-aware fetch wrapper
const timeoutFetch = async (url, options = {}) => {
  const controller = new AbortController();
  const timeout = options.timeout || 20000;
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } finally {
    clearTimeout(id);
  }
};

// Create GraphQL client instance
const graphqlClient = new GraphQLClient(config.GRAPHQL_URL, {
  headers: {
    'Content-Type': 'application/json',
  },
  fetch: (url, options) => timeoutFetch(url, { ...options, timeout: 30000 }),
});

const withTicketAuthorization = () => {
  const token = sessionStorage.getItem('ticketUserToken')
  graphqlClient.setHeader('Authorization', token ? `Bearer ${token}` : '')
}

// GraphQL Queries
export const GET_PURCHASABLE_EVENTS = gql`
  query GetPurchasableEvents {
    purchasableEvents {
      id
      name
      date
      summary
      venue_name
      venue_address
      start_time
      open
      early_fee
      late_fee
      isSoldOut
      meetup_id
      RSVP_No
      RSVP_No_LastMinute
      NoShows
      options
      soldTickets
      maxTickets
      remainingTickets
    }
  }
`;

export const GET_EVENT_BY_ID = gql`
  query GetEventById($id: ID!) {
    event(id: $id) {
      id
      name
      date
      summary
      venue_name
      venue_address
      start_time
      open
      early_fee
      late_fee
      isSoldOut
      meetup_id
      RSVP_No
      RSVP_No_LastMinute
      NoShows
      options
    }
  }
`;

export const GET_EVENT_PRICING = gql`
  query GetEventPricing($eventId: ID!) {
    eventPricing(eventId: $eventId) {
      eventId
      currentPrice
      earlyPrice
      latePrice
      isEarlyBird
      daysUntilEvent
    }
  }
`;

export const GET_TICKET_AVAILABILITY = gql`
  query GetTicketAvailability($eventId: ID!) {
    ticketAvailability(eventId: $eventId) {
      eventId
      available
      maxTickets
      soldTickets
      remainingTickets
    }
  }
`;

export const GET_ORDER = gql`
  query GetOrder($orderId: String!) {
    order(orderId: $orderId) {
      orderId
      eventId
      eventName
      eventDate
      email
      quantity
      pricePerTicket
      total
      status
      createdAt
      token
      transactionStatus
      transactionAmount
      transactionOrderId
      transactionCreatedAt
      transactionQuantity
    }
  }
`;

export const GET_MEMBER_BY_EMAIL = gql`
  query GetMemberByEmail($email: String!) {
    memberByEmail(email: $email) {
      id
      name
      category
    }
  }
`;

// GraphQL Mutations
export const SEND_VERIFICATION_CODE = gql`
  mutation SendVerificationCode($email: String!, $eventId: ID!, $quantity: Int!, $locale: String) {
    sendVerificationCode(email: $email, eventId: $eventId, quantity: $quantity, locale: $locale)
  }
`;

export const SEND_HISTORY_VERIFICATION_CODE = gql`
  mutation SendHistoryVerificationCode($email: String!, $locale: String) {
    sendHistoryVerificationCode(email: $email, locale: $locale)
  }
`;

export const VERIFY_EMAIL_CODE = gql`
  mutation VerifyEmailCode($email: String!, $code: String!) {
    verifyEmailCode(email: $email, code: $code) {
      success
      message
      token
    }
  }
`;

export const CREATE_TICKET_ORDER = gql`
  mutation CreateTicketOrder(
    $eventId: ID!
    $email: String!
    $quantity: Int!
  ) {
    createTicketOrder(
      eventId: $eventId
      email: $email
      quantity: $quantity
    ) {
      orderId
      eventId
      email
      quantity
      pricePerTicket
      total
      status
      createdAt
      token
    }
  }
`;

export const GENERATE_PAYMENT = gql`
  mutation GeneratePayment($orderId: String!, $quantity: Int, $total: Float) {
    generatePayment(orderId: $orderId, quantity: $quantity, total: $total) {
      orderId
      merchantParameters
      signature
      redsysUrl
      fuc
      terminal
      merchantOrder
    }
  }
`;

/**
 * GraphQL API client with methods for events and tickets
 */
export const graphqlAPI = {
  // Event queries
  async getPurchasableEvents() {
    try {
      const data = await graphqlClient.request(GET_PURCHASABLE_EVENTS);
      return data.purchasableEvents;
    } catch (error) {
      console.error('Error fetching purchasable events:', error);
      throw error;
    }
  },

  async getMemberByEmail(email) {
    try {
      const data = await graphqlClient.request(GET_MEMBER_BY_EMAIL, { email });
      return data.memberByEmail;
    } catch (error) {
      console.error('Error fetching member:', error);
      return null;
    }
  },

  async getEventById(id) {
    try {
      const data = await graphqlClient.request(GET_EVENT_BY_ID, { id });
      return data.event;
    } catch (error) {
      console.error('Error fetching event by ID:', error);
      throw error;
    }
  },

  async getEventPricing(eventId) {
    try {
      const data = await graphqlClient.request(GET_EVENT_PRICING, { eventId });
      return data.eventPricing;
    } catch (error) {
      console.error('Error fetching event pricing:', error);
      throw error;
    }
  },

  async getTicketAvailability(eventId) {
    try {
      const data = await graphqlClient.request(GET_TICKET_AVAILABILITY, { eventId });
      return data.ticketAvailability;
    } catch (error) {
      console.error('Error fetching ticket availability:', error);
      throw error;
    }
  },

  async getOrder(orderId) {
    try {
      withTicketAuthorization()
      const data = await graphqlClient.request(GET_ORDER, { orderId });
      return data.order;
    } catch (error) {
      console.error('Error fetching order:', error);
      throw error;
    }
  },

  // Mutations
  async sendVerificationCode(email, eventId, quantity, locale) {
    try {
      const data = await graphqlClient.request(SEND_VERIFICATION_CODE, {
        email,
        eventId,
        quantity,
        locale
      });
      return data.sendVerificationCode;
    } catch (error) {
      console.error('Error sending verification code:', error);
      throw error;
    }
  },

  async sendHistoryVerificationCode(email, locale) {
    try {
      const data = await graphqlClient.request(SEND_HISTORY_VERIFICATION_CODE, {
        email,
        locale
      });
      return data.sendHistoryVerificationCode;
    } catch (error) {
      console.error('Error sending history verification code:', error);
      throw error;
    }
  },

  async verifyEmailCode(email, code) {
    try {
      const data = await graphqlClient.request(VERIFY_EMAIL_CODE, {
        email,
        code
      });
      return data.verifyEmailCode;
    } catch (error) {
      console.error('Error verifying email code:', error);
      throw error;
    }
  },

  async createTicketOrder(eventId, email, quantity, pricePerTicket, total) {
    try {
      withTicketAuthorization()
      const data = await graphqlClient.request(CREATE_TICKET_ORDER, {
        eventId,
        email,
        quantity
      });
      return data.createTicketOrder;
    } catch (error) {
      console.error('Error creating ticket order:', error);
      throw error;
    }
  },

  async generatePayment(orderId, quantity, total) {
    try {
      withTicketAuthorization()
      const variables = { orderId };
      if (typeof quantity === 'number') variables.quantity = quantity;
      if (typeof total === 'number') variables.total = total;
      const data = await graphqlClient.request(GENERATE_PAYMENT, variables);
      return data.generatePayment;
    } catch (error) {
      console.error('Error generating payment:', error);
      throw error;
    }
  }
};

export default graphqlAPI;
